import {
  ImageSliceView,
  ImageSliceViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { getCreativeSuffix } from 'src/lib/utils';
import popups from 'src/lib/ui/config/popups';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { getTimestampText } from '../visit.util';
import { Feature } from 'src/replicant/analytics';
import { Avatar, AvatarOpts } from 'src/game/components/shared/Avatar';
import StateObserver from 'src/StateObserver';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import sounds from 'src/lib/sounds';
import { sendAddFriendRequest } from 'src/game/friends/friends.sequences';
import {
  canAddFriends,
  isBuruFriend,
  isFriendshipRequestedSent,
} from 'src/replicant/friends/friends.getters';
import { AvatarFrameId } from 'src/replicant/profile/frames';

type Player = {
  id: string;
  name?: string;
  photo?: string;
  frame: AvatarFrameId;
  species: PetSpeciesID;
  canAcceptFriendRequest: boolean;
  petState?: string;
};
type Friend = Player & {
  lastLogin: number;
};
type Visitor = Player & {
  lastVisit: number;
};
const playerIsVisitor = (player: Player): player is Visitor =>
  'lastVisit' in player;

namespace style {
  export const container: Omit<ButtonScaleViewOpts, 'tracking'> = {
    offsetX: 20,
    offsetY: 10,
    pressedOffsetY: 5,
    width: popups.buruburu.width - 100,
    height: 114,
  };

  export const tile: ImageSliceViewOpts = {
    image: 'assets/ui/shared/frames/scroller_panel_blue.png',
    width: container.width,
    height: container.height,
    slices: {
      top: 19,
      left: 19,
      right: 22,
      bot: 28,
    },
  };

  export const tileGrey: ImageSliceViewOpts = {
    ...tile,
    image: 'assets/ui/shared/frames/scroller_panel_grey.png',
  };

  export const image: AvatarOpts = {
    width: 78,
    height: 78,
    x: 16,
    y: 15,
    centerOnOrigin: false,
  };

  export const name: LangBitmapOpts = {
    size: 30,
    font: 'Body',
    color: '#9A7171',
    verticalAlign: 'top',
    width: 211,
    height: 48,
    x: 115,
    y: 18,
  };

  export const speciesLabel: LangBitmapOpts = {
    size: 22,
    font: 'Body',
    color: '#7191AB',
    x: name.x,
    y: 59,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const speciesIconBackdrop: ImageViewOpts = {
    image: 'assets/ui/shared/frames/backdrop_panel.png',
    x: image.x! + image.width!,
    y: image.y! + image.height!,
    width: 128,
    height: 133,
    scale: 0.33,
  };
  speciesIconBackdrop.x! -=
    speciesIconBackdrop.width! * speciesIconBackdrop.scale! * 0.7;
  speciesIconBackdrop.y! -=
    speciesIconBackdrop.height! * speciesIconBackdrop.scale! * 0.9;

  export const speciesIcon: ImageScaleViewOpts = {
    width: speciesIconBackdrop.width,
    height: speciesIconBackdrop.height,
    scaleMethod: 'contain',
  };

  export const lastLogin: LangBitmapOpts = {
    font: speciesLabel.font,
    color: speciesLabel.color,
    y: 53,
    height: 38,
    width: 160,
    size: 16,
    align: 'right',
    verticalAlign: 'bottom',
  };
  lastLogin.x = container.width! - container.offsetX! - lastLogin.width!;

  export const addFriendButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> =
    {
      image: 'assets/ui/friends/button_secondary_small.png',
      imagePressed: 'assets/ui/friends/button_secondary_small_pressed.png',
      scaleMethod: '9slice' as const,
      sourceSlices: {
        horizontal: { left: 38, right: 38 },
        vertical: { top: 24, bottom: 24 },
      },
      pressedOffsetY: 5,
      width: 164,
      height: 42,
      x: container.width! - 180,
      y: 10,
      fontSize: 24,
      font: bitmapFonts('Body'),
    };

  export const requestedLabelFrame: ImageViewOpts = {
    image: 'assets/ui/friends/requested_label.png',
    x: addFriendButton.x,
    y: addFriendButton.y,
    width: addFriendButton.width!,
    height: addFriendButton.height!,
  };

  export const requestedLabel: LangBitmapOpts = {
    font: 'Body',
    color: '#7191AB',
    x: requestedLabelFrame.width! / 2,
    y: requestedLabelFrame.height! / 2,
    centerOnOrigin: true,
    width: 120,
    height: 29,
    size: 24,
    align: 'center',
    verticalAlign: 'center',
  };
}

export class VisitFriendTile extends ButtonScaleView {
  private friend?: Friend | Visitor = undefined;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly species: LangBitmapFontTextView;
  private readonly speciesIcon: ImageScaleView;
  private readonly timestamp: LangBitmapFontTextView;
  private readonly addFriend!: ButtonScaleViewWithText;
  private readonly requestedLabel!: ImageView;
  private readonly bg: ImageSliceView;
  private createRequestedLabelText?: LangBitmapFontTextView;

  private readonly tab: 'friends' | 'visitors';

  constructor(opts: {
    friend: Friend | Visitor;
    tab: 'friends' | 'visitors';
    onClick: () => Promise<void>;
  }) {
    super({
      ...style.container,
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.SELECT,
      },
    });

    this.friend = opts.friend;
    this.tab = opts.tab;

    // background
    this.bg = this.addSubview(new ImageSliceView(style.tile));

    // icon
    this.avatar = this.addSubview(
      new Avatar({
        ...style.image,
        centerOnOrigin: false,
      }),
    );

    // name
    this.name = this.addSubview(
      new LangBitmapFontTextView({
        ...style.name,
        width: style.container.width! - 268,
      }),
    );

    // species name
    this.species = this.addSubview(
      new LangBitmapFontTextView({
        ...style.speciesLabel,
      }),
    );

    // species icon
    const speciesIcon = this.addSubview(
      new ImageView(style.speciesIconBackdrop),
    );
    this.speciesIcon = speciesIcon.addSubview(
      new ImageScaleView({
        ...style.speciesIcon,
      }),
    );

    // timestamp
    this.timestamp = this.addSubview(
      new LangBitmapFontTextView({
        ...style.lastLogin,
      }),
    );

    // Instead addFriend button, if user already added friend show requested label
    this.requestedLabel = this.addSubview(this.createRequestedLabel());

    // Add friend button
    this.addFriend = this.addSubview(this.createAddFriendButton());

    this.setData(opts.friend, opts.onClick);
  }

  /**
   * Create requested label
   */
  private createRequestedLabel() {
    const frame = new ImageView({
      ...style.requestedLabelFrame,
      visible: false,
    });

    this.createRequestedLabelText = new LangBitmapFontTextView({
      superview: frame,
      ...style.requestedLabel,
      localeText: () => i18n('friends.requested'),
    });

    return frame;
  }

  /**
   * Create add friend button
   */
  private createAddFriendButton() {
    return new ButtonScaleViewWithText({
      ...style.addFriendButton,
      visible: false,
      preventDefault: true,
      localeText: () => i18n('friends.addFriend'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.onAddFriend(this.friend!);
      },
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.ADD_FRIEND,
      },
    });
  }

  /**
   * On add friend button click
   */
  private async onAddFriend(player: Friend | Visitor) {
    const result = await sendAddFriendRequest(
      {
        id: player.id,
        name: player.name ?? '',
        profilePicture: player.photo ?? '',
        frame: player.frame ?? 'default',
        species: player.species ?? 'ball',
      },
      {
        sourceFeature: 'visit',
        originFeature: 'visit',
        originSubFeature:
          this.tab === 'visitors' ? 'visit_visitors' : 'visit_suggestions',
      },
    );

    this.requestedLabel.style.visible = result;
    this.addFriend.style.visible = !result;
  }

  public setData(friend: Friend | Visitor, onClick: () => Promise<void>) {
    this.friend = friend;
    const { species, photo, name, frame } = friend;

    const { user } = StateObserver.getState();
    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];
    const isDead = friend.petState === 'dead';

    this.avatar.updateImage(photo!);
    this.avatar.setFrame(frame);
    this.name.localeText = () => name ?? '';
    this.species.localeText = () =>
      evolutions[species]?.[`name_${getCreativeSuffix()}`];

    this.speciesIcon.updateOpts({
      image: `assets/species/${species}/${isDead ? 'dead' : 'icon'}.png`,
    });

    this.timestamp.localeText = () =>
      playerIsVisitor(friend)
        ? `${i18n('friendsVisit.popup.lastVisit')}\n${getTimestampText(
            friend.lastVisit,
          )}`
        : `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
            friend.lastLogin,
          )}`;

    // Already friends, hide add friend UI
    if (isBuruFriend(user, friend.id) || !canAddFriends(user)) {
      this.addFriend.style.visible = false;
      this.requestedLabel.style.visible = false;
    } else if (isFriendshipRequestedSent(user, friend.id)) {
      // Already sent friend request, show requested label only
      this.addFriend.style.visible = false;
      this.requestedLabel.style.visible = true;
    } else {
      // Show add friend button only
      this.addFriend.style.visible = friend.canAcceptFriendRequest;
      this.requestedLabel.style.visible = false;
    }

    if (isDead) {
      this.bg.updateOpts({ ...style.tileGrey });
      this.name.updateOpts({ color: '#FFFFFF' });
      this.species.updateOpts({ color: '#E1C9DC' });
      this.timestamp.updateOpts({ color: '#E1C9DC' });
      this.createRequestedLabelText?.updateOpts({ color: '#3B5098' });
    } else {
      this.bg.updateOpts({ ...style.tile });
      this.name.updateOpts({ ...style.name });
      this.species.updateOpts({ ...style.speciesLabel });
      this.timestamp.updateOpts({ ...style.lastLogin });
      this.createRequestedLabelText?.updateOpts({ ...style.requestedLabel });
    }

    this.onClick = onClick;
  }
}
